import React, { useState, useId } from "react";

export const CustomRadioGroup = ({
    children,
    defaultValue,
    name,
    onChange,
    value: controlledValue,
    className,
}) => {
    const [uncontrolledValue, setUncontrolledValue] = useState(
        defaultValue ?? ""
    );
    const groupName = name || useId();

    // Controlled vs Uncontrolled value
    const value =
        controlledValue !== undefined ? controlledValue : uncontrolledValue;

    const handleChange = (event) => {
        if (controlledValue === undefined) {
            setUncontrolledValue(event.target.value);
        }
        onChange?.(event, event.target.value);
    };

    // Handle div click to select radio
    const handleDivClick = (radioValue, isDisabled) => (event) => {
        // Don't allow clicks on disabled items
        if (isDisabled) return;

        // Prevent double triggering if clicking directly on radio
        if (event.target.type === "radio") return;

        // Create synthetic event
        const syntheticEvent = {
            target: {
                value: radioValue,
                type: "radio",
            },
        };
        handleChange(syntheticEvent);
    };

    // Clone children & inject props
    const clonedChildren = React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;

        // If it's a div containing radio input, make it clickable
        if (child.type === "div" && child.props.children) {
            const radioInput = React.Children.toArray(
                child.props.children
            ).find(
                (grandChild) =>
                    React.isValidElement(grandChild) &&
                    grandChild.type === "input" &&
                    grandChild.props.type === "radio"
            );

            if (radioInput) {
                const isDisabled = radioInput.props.disabled;
                const isChecked =
                    String(value) === String(radioInput.props.value);
                return React.cloneElement(child, {
                    onClick: handleDivClick(radioInput.props.value, isDisabled),
                    style: {
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                        ...child.props.style,
                    },
                    children: React.Children.map(
                        child.props.children,
                        (grandChild) => {
                            if (
                                React.isValidElement(grandChild) &&
                                grandChild.type === "input" &&
                                grandChild.props.type === "radio"
                            ) {
                                return React.cloneElement(grandChild, {
                                    name: groupName,
                                    checked: isChecked,
                                    onChange: handleChange,
                                    disabled: grandChild.props.disabled, // Preserve disabled state
                                    className: `w-4 h-4 ${
                                        grandChild.props.className || ""
                                    }`,
                                    style: {
                                        accentColor: isChecked
                                            ? "#c50000"
                                            : undefined,
                                        ...grandChild.props.style,
                                    },
                                });
                            }
                            return grandChild;
                        }
                    ),
                });
            }
        }

        // If it's a direct radio input, wrap it in a clickable div
        if (child.type === "input" && child.props.type === "radio") {
            const isDisabled = child.props.disabled;
            const isChecked = String(value) === String(child.props.value);
            return (
                <div
                    onClick={handleDivClick(child.props.value, isDisabled)}
                    style={{
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                    }}
                >
                    {React.cloneElement(child, {
                        name: groupName,
                        checked: isChecked,
                        onChange: handleChange,
                        disabled: child.props.disabled, // Preserve disabled state
                        className: `w-4 h-4 ${child.props.className || ""}`,
                        style: {
                            accentColor: isChecked ? "#c50000" : undefined,
                            ...child.props.style,
                        },
                    })}
                </div>
            );
        }

        return child;
    });

    return (
        <div role="radiogroup" className={className}>
            {clonedChildren}
        </div>
    );
};
